import { useState } from 'react'

import { revalidateLogic } from '@tanstack/react-form'

import { useAppForm } from '@ui/components/forms'
import { Button } from '@ui/components/shadcn/ui/button'
import { FieldGroup } from '@ui/components/shadcn/ui/field'

import {
  useAuthAnalytics,
  useAuthScreenView,
} from '@web-app/components/auth/analytics'
import { signInWithSmsCode } from '@web-app/lib/auth/client'
import {
  GENERIC_ERROR,
  transportFailureCopy,
} from '@web-app/lib/errors/transportFailure'
import { otpCodeSchema, phoneSchema } from '@web-app/schemas/auth'

import { useSendSMSLoginCode } from './useSendSMSLoginCode'

const RESEND_INTERVAL_MS = 60_000

const obscurePhone = (digits: string) => `(•••) •••-${digits.slice(-4)}`

interface Props {
  initialPhone?: string
  // When the SMS was already sent for initialPhone (the hero sends it on
  // Continue), the code step opens directly.
  smsSentAt?: number
  redirect?: string
  // The route answers where the signed-in user goes next.
  onSuccess: (destination: string) => void | Promise<void>
  onUseEmail: () => void
}

export function PhoneLoginForm({
  initialPhone = '',
  smsSentAt,
  redirect,
  onSuccess,
  onUseEmail,
}: Props) {
  const [sent, setSent] = useState<{ phone: string; at: number } | null>(
    initialPhone && smsSentAt ? { phone: initialPhone, at: smsSentAt } : null,
  )

  return sent ? (
    <CodeStep
      phone={sent.phone}
      sentAt={sent.at}
      redirect={redirect}
      onResent={(at) => setSent({ phone: sent.phone, at })}
      onSuccess={onSuccess}
      onChangePhone={() => setSent(null)}
    />
  ) : (
    <PhoneStep
      initialPhone={initialPhone}
      onSent={(phone) => setSent({ phone, at: Date.now() })}
      onUseEmail={onUseEmail}
    />
  )
}

function PhoneStep({
  initialPhone,
  onSent,
  onUseEmail,
}: {
  initialPhone: string
  onSent: (phone: string) => void
  onUseEmail: () => void
}) {
  const sendSmsLoginCode = useSendSMSLoginCode()

  const form = useAppForm({
    defaultValues: { phone: initialPhone },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: phoneSchema,
      onSubmitAsync: async ({ value }) => {
        if (!(await sendSmsLoginCode(value.phone))) {
          return { form: GENERIC_ERROR }
        }
        onSent(value.phone)
        return null
      },
    },
  })

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <FieldGroup className="gap-2">
        <form.AppField name="phone">
          {(field) => <field.PhoneField />}
        </form.AppField>
      </FieldGroup>
      <form.AppForm>
        <div className="mt-2 flex flex-col gap-2">
          <form.SubmitButton>Send code</form.SubmitButton>
          <form.FormError />
        </div>
      </form.AppForm>
      <p className="text-center text-sm">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm"
          onClick={onUseEmail}
        >
          Sign in with email instead
        </Button>
      </p>
    </form>
  )
}

function CodeStep({
  phone,
  sentAt,
  redirect,
  onResent,
  onSuccess,
  onChangePhone,
}: {
  phone: string
  sentAt: number
  redirect?: string
  onResent: (at: number) => void
  onSuccess: (destination: string) => void | Promise<void>
  onChangePhone: () => void
}) {
  const sendSmsLoginCode = useSendSMSLoginCode()
  const { trackTap } = useAuthAnalytics()
  useAuthScreenView('loginSmsCode')

  const form = useAppForm({
    defaultValues: { code: '' },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: otpCodeSchema,
      onSubmitAsync: async ({ value }) => {
        trackTap('loginSmsCodeNext')
        const outcome = await signInWithSmsCode({
          phone,
          code: value.code,
          redirect,
        })
        switch (outcome.kind) {
          case 'signed-in':
            await onSuccess(outcome.destination)
            return null
          case 'rejected':
            return {
              fields: { code: { message: 'Invalid verification code' } },
            }
          case 'unreachable':
            return { form: transportFailureCopy() }
          case 'failed':
            return { form: GENERIC_ERROR }
        }
      },
    },
  })

  const resend = async () => {
    trackTap('loginSmsCodeResend')
    if (Date.now() - sentAt < RESEND_INTERVAL_MS) {
      form.setErrorMap({
        onSubmit: {
          form: 'Please wait a minute before requesting another code.',
        },
      })
      return
    }
    if (await sendSmsLoginCode(phone)) {
      form.setErrorMap({ onSubmit: undefined })
      onResent(Date.now())
    } else {
      form.setErrorMap({ onSubmit: { form: GENERIC_ERROR } })
    }
  }

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <p className="text-muted-foreground mb-6 text-center text-sm">
        We texted a code to{' '}
        <span className="text-foreground font-medium">
          {obscurePhone(phone)}
        </span>
        .{' '}
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm underline"
          onClick={() => {
            trackTap('loginSmsCodeBack')
            onChangePhone()
          }}
        >
          Wrong number?
        </Button>
      </p>
      <FieldGroup className="gap-2">
        <form.AppField name="code">
          {(field) => <field.OtpField />}
        </form.AppField>
      </FieldGroup>
      <form.AppForm>
        <div className="mt-2 flex flex-col gap-2">
          <form.SubmitButton>Sign in</form.SubmitButton>
          <form.FormError />
        </div>
      </form.AppForm>
      <p className="text-center text-sm">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm"
          onClick={() => void resend()}
        >
          Resend code
        </Button>
      </p>
    </form>
  )
}
